export const PENDING_AGENT_ACTIVATION_KEY = "pending-agent-activation";
export const PENDING_FEISHU_IDENTITY_SYNC_KEY = "pending-feishu-identity-sync";
export const KNOWLEDGE_AUTHORIZED_AT_KEY = "knowledge-authorized-at";
export const FEISHU_CONNECTOR_AUTHORIZED_AT_KEY = "feishu-connector-authorized-at";
export const FEISHU_WRITE_AUTHORIZED_AT_KEY = "feishu-calendar-write-authorized-at-v2";
export const FEISHU_OFFICE_AUTHORIZED_AT_KEY = "feishu-office-authorized-at-v1";
export const FEISHU_OFFICE_WRITE_AUTHORIZED_AT_KEY = "feishu-office-write-authorized-at-v1";
export const KNOWLEDGE_AUTH_ENTRY_PATH = "/agents";

export interface PendingAgentActivation {
  id: string;
  name: string;
  requiresKnowledgeAuthorization: boolean;
  requiresFeishuConnectorAuthorization: boolean;
  requiresFeishuWriteAuthorization: boolean;
  requiresFeishuOfficeAuthorization: boolean;
  requiresFeishuOfficeWriteAuthorization: boolean;
}

export interface PendingFeishuIdentitySync {
  returnPath: string;
}

const KNOWLEDGE_AUTH_FRESH_MS = 30 * 60 * 1000;

export function hasRecentKnowledgeAuthorization(
  storage: Pick<Storage, "getItem">,
  now = Date.now()
) {
  const authorizedAt = Number(storage.getItem(KNOWLEDGE_AUTHORIZED_AT_KEY));
  return Number.isFinite(authorizedAt)
    && authorizedAt > 0
    && now - authorizedAt < KNOWLEDGE_AUTH_FRESH_MS;
}

export function hasRecentFeishuConnectorAuthorization(
  storage: Pick<Storage, "getItem">,
  now = Date.now()
) {
  const authorizedAt = Number(storage.getItem(FEISHU_CONNECTOR_AUTHORIZED_AT_KEY));
  return Number.isFinite(authorizedAt)
    && authorizedAt > 0
    && now - authorizedAt < KNOWLEDGE_AUTH_FRESH_MS;
}

export function hasRecentFeishuWriteAuthorization(
  storage: Pick<Storage, "getItem">,
  now = Date.now()
) {
  const authorizedAt = Number(storage.getItem(FEISHU_WRITE_AUTHORIZED_AT_KEY));
  return Number.isFinite(authorizedAt)
    && authorizedAt > 0
    && now - authorizedAt < KNOWLEDGE_AUTH_FRESH_MS;
}

export function hasRecentFeishuOfficeAuthorization(
  storage: Pick<Storage, "getItem">,
  now = Date.now()
) {
  const authorizedAt = Number(storage.getItem(FEISHU_OFFICE_AUTHORIZED_AT_KEY));
  return Number.isFinite(authorizedAt)
    && authorizedAt > 0
    && now - authorizedAt < KNOWLEDGE_AUTH_FRESH_MS;
}

export function hasRecentFeishuOfficeWriteAuthorization(
  storage: Pick<Storage, "getItem">,
  now = Date.now()
) {
  const authorizedAt = Number(storage.getItem(FEISHU_OFFICE_WRITE_AUTHORIZED_AT_KEY));
  return Number.isFinite(authorizedAt)
    && authorizedAt > 0
    && now - authorizedAt < KNOWLEDGE_AUTH_FRESH_MS;
}

export function savePendingAgentActivation(
  storage: Pick<Storage, "setItem">,
  activation: PendingAgentActivation
) {
  storage.setItem(PENDING_AGENT_ACTIVATION_KEY, JSON.stringify(activation));
}

export function consumePendingAgentActivation(
  storage: Pick<Storage, "getItem" | "removeItem">
): PendingAgentActivation | undefined {
  const raw = storage.getItem(PENDING_AGENT_ACTIVATION_KEY);
  storage.removeItem(PENDING_AGENT_ACTIVATION_KEY);
  if (!raw) return undefined;
  try {
    const parsed = JSON.parse(raw) as Partial<PendingAgentActivation>;
    if (typeof parsed.id !== "string" || typeof parsed.name !== "string") return undefined;
    return {
      id: parsed.id,
      name: parsed.name,
      requiresKnowledgeAuthorization: parsed.requiresKnowledgeAuthorization === true,
      requiresFeishuConnectorAuthorization: parsed.requiresFeishuConnectorAuthorization === true,
      requiresFeishuWriteAuthorization: parsed.requiresFeishuWriteAuthorization === true,
      requiresFeishuOfficeAuthorization: parsed.requiresFeishuOfficeAuthorization === true,
      requiresFeishuOfficeWriteAuthorization: parsed.requiresFeishuOfficeWriteAuthorization === true
    };
  } catch {
    return undefined;
  }
}

export function savePendingFeishuIdentitySync(
  storage: Pick<Storage, "setItem">,
  returnPath: string
) {
  storage.setItem(PENDING_FEISHU_IDENTITY_SYNC_KEY, JSON.stringify({ returnPath }));
}

export function consumePendingFeishuIdentitySync(
  storage: Pick<Storage, "getItem" | "removeItem">
): PendingFeishuIdentitySync | undefined {
  const raw = storage.getItem(PENDING_FEISHU_IDENTITY_SYNC_KEY);
  storage.removeItem(PENDING_FEISHU_IDENTITY_SYNC_KEY);
  if (!raw) return undefined;
  try {
    const parsed = JSON.parse(raw) as Partial<PendingFeishuIdentitySync>;
    if (
      typeof parsed.returnPath !== "string"
      || !/^\/agents\/[A-Za-z0-9-]+\/edit(?:\?.*)?$/.test(parsed.returnPath)
    ) return undefined;
    return { returnPath: parsed.returnPath };
  } catch {
    return undefined;
  }
}

export function shouldReturnToKnowledgeEntry(
  requiresKnowledgeAuthorization: boolean,
  currentPath: string,
  recentlyAuthorized: boolean
) {
  return requiresKnowledgeAuthorization
    && !recentlyAuthorized
    && currentPath !== KNOWLEDGE_AUTH_ENTRY_PATH;
}

export function shouldReturnToFeishuAuthEntry(
  needsKnowledgeAuthorization: boolean,
  needsConnectorAuthorization: boolean,
  currentPath: string
) {
  return (needsKnowledgeAuthorization || needsConnectorAuthorization)
    && currentPath !== KNOWLEDGE_AUTH_ENTRY_PATH;
}
